import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaSearch, FaEdit, FaTrash, FaMapMarkerAlt } from 'react-icons/fa';
import { API_BASE } from '../../utils/helpers';

const emptyLoc = { id: null, zone: '', municipality: '', district: '', delivery_fee: '20', free_delivery_order_amount: '200', status: true };

const DeliveryLocations = () => {
  const [locations, setLocations] = useState([]);
  const [search, setSearch] = useState('');
  const [filtered, setFiltered] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [newLocation, setNewLocation] = useState(emptyLoc);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => { fetchLocations(); }, []);

  useEffect(() => {
    const q = (search || '').toLowerCase();
    setFiltered(locations.filter((loc) =>
      String(loc.zone || '').toLowerCase().includes(q) ||
      String(loc.municipality || '').toLowerCase().includes(q) ||
      String(loc.district || '').toLowerCase().includes(q)
    ));
    setCurrentPage(1);
  }, [search, locations]);

  const fetchLocations = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/delivery-settings`);
      setLocations(res.data || []);
      setFiltered(res.data || []);
    } catch (error) { console.error('Error fetching locations:', error); }
  };

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginatedData = () => filtered.slice((currentPage - 1) * itemsPerPage, (currentPage - 1) * itemsPerPage + itemsPerPage);

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewLocation((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };
  const resetMessageAfter = (ms = 2500) => setTimeout(() => setMessage({ type: '', text: '' }), ms);

  const submitNewLocation = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    if (!newLocation.zone || !newLocation.municipality || !newLocation.district) {
      setMessage({ type: 'error', text: 'Please fill Zone, Municipality and District.' }); resetMessageAfter(); return;
    }
    const payload = {
      zone: newLocation.zone, municipality: newLocation.municipality, district: newLocation.district,
      delivery_fee: Number(newLocation.delivery_fee || 0),
      free_delivery_order_amount: Number(newLocation.free_delivery_order_amount || 200),
      status: Boolean(newLocation.status),
    };
    try {
      setSaving(true);
      const body = isEditing ? { ...payload, id: newLocation.id } : payload;
      await axios.post(`${API_BASE}/api/delivery-settings`, body);
      setMessage({ type: 'success', text: isEditing ? 'Location updated successfully.' : 'Location added successfully.' });
      setNewLocation(emptyLoc); setIsEditing(false); await fetchLocations();
    } catch (error) {
      console.error('Error adding location:', error);
      setMessage({ type: 'error', text: error?.response?.data?.message || 'Failed to add location.' });
    } finally { setSaving(false); resetMessageAfter(); }
  };

  const deleteLocation = async (id) => {
    if (!id || !window.confirm('Delete this location? This action cannot be undone.')) return;
    try {
      setDeletingId(id);
      await axios.delete(`${API_BASE}/api/delivery-settings/delivery-locations/${id}`);
      setMessage({ type: 'success', text: 'Location deleted.' }); await fetchLocations();
    } catch (error) {
      setMessage({ type: 'error', text: error?.response?.data?.message || 'Failed to delete location.' });
    } finally { setDeletingId(null); resetMessageAfter(); }
  };

  const startEdit = (loc) => {
    setNewLocation({
      id: loc.id, zone: String(loc.zone ?? ''), municipality: String(loc.municipality ?? ''),
      district: String(loc.district ?? ''), delivery_fee: String(loc.delivery_fee ?? '0'),
      free_delivery_order_amount: String(loc.free_delivery_order_amount ?? '200'), status: !!loc.status,
    });
    setIsEditing(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-900">Delivery Locations</h2>
        <p className="text-sm text-slate-500">Zones, fees and free-delivery thresholds.</p>
      </div>

      {message.text && (
        <div className={`mb-4 rounded-lg border px-4 py-2.5 text-sm ${
          message.type === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-rose-200 bg-rose-50 text-rose-700'
        }`}>{message.text}</div>
      )}

      {/* Form */}
      <form onSubmit={submitNewLocation} className="ui-card mb-6 p-5">
        <h3 className="mb-4 text-sm font-semibold text-slate-900">{isEditing ? 'Edit location' : 'Add location'}</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Zone</label>
            <input className="ui-input" name="zone" value={newLocation.zone} onChange={handleFormChange} placeholder="e.g., 91" required />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Municipality</label>
            <input className="ui-input" name="municipality" value={newLocation.municipality} onChange={handleFormChange} placeholder="Municipality" required />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">District</label>
            <input className="ui-input" name="district" value={newLocation.district} onChange={handleFormChange} placeholder="District" required />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Delivery Fee (QAR)</label>
            <input className="ui-input" type="number" step="0.01" min="0" name="delivery_fee" value={newLocation.delivery_fee} onChange={handleFormChange} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Free Delivery Order Amount</label>
            <input className="ui-input" type="number" step="0.01" min="0" name="free_delivery_order_amount" value={newLocation.free_delivery_order_amount} onChange={handleFormChange} />
          </div>
          <label className="flex items-center gap-2 pt-7 text-sm text-slate-700">
            <input type="checkbox" name="status" className="h-4 w-4 rounded border-slate-300 text-indigo-600" checked={!!newLocation.status} onChange={handleFormChange} />
            Enabled
          </label>
        </div>
        <div className="mt-4 flex gap-2">
          <button type="submit" className="ui-btn-primary" disabled={saving}>{saving ? 'Saving…' : isEditing ? 'Save changes' : 'Add location'}</button>
          {isEditing && <button type="button" className="ui-btn-ghost" onClick={() => { setNewLocation(emptyLoc); setIsEditing(false); }} disabled={saving}>Cancel</button>}
        </div>
      </form>

      {/* Search */}
      <div className="ui-card mb-5 p-4">
        <div className="relative max-w-md">
          <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400" />
          <input className="ui-input pl-9" type="text" placeholder="Search zone, municipality, district…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      {/* Table */}
      <div className="ui-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="ui-table">
            <thead>
              <tr>
                <th className="w-12">#</th><th>Zone</th><th>Municipality</th><th>District</th>
                <th>Delivery Fee</th><th>Free Delivery ≥</th><th>Status</th><th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <div className="flex flex-col items-center justify-center gap-2 py-12 text-slate-400">
                      <FaMapMarkerAlt className="text-2xl" />
                      <p className="text-sm">No matching delivery locations found.</p>
                    </div>
                  </td>
                </tr>
              ) : paginatedData().map((loc, index) => (
                <tr key={loc.id}>
                  <td className="text-slate-400">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                  <td className="font-medium text-slate-900">{loc.zone}</td>
                  <td className="text-slate-500">{loc.municipality}</td>
                  <td className="text-slate-500">{loc.district}</td>
                  <td className="whitespace-nowrap">QAR {parseFloat(loc.delivery_fee).toFixed(2)}</td>
                  <td className="whitespace-nowrap text-slate-500">QAR {loc.free_delivery_order_amount}</td>
                  <td>
                    <span className={`ui-badge ${loc.status ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                      {loc.status ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center justify-end gap-2">
                      <button className="ui-btn-ghost px-2.5 py-1.5 text-xs" onClick={() => startEdit(loc)}><FaEdit className="text-xs" /> Edit</button>
                      <button className="ui-btn-ghost px-2.5 py-1.5 text-xs text-rose-600 hover:bg-rose-50" onClick={() => deleteLocation(loc.id)} disabled={deletingId === loc.id}>
                        <FaTrash className="text-xs" /> {deletingId === loc.id ? 'Deleting…' : 'Delete'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex flex-wrap items-center justify-center gap-1 border-t border-slate-200 px-4 py-3">
            {[...Array(totalPages)].map((_, idx) => {
              const page = idx + 1;
              const active = page === currentPage;
              return (
                <button key={page} onClick={() => setCurrentPage(page)}
                  className={`h-8 min-w-8 rounded-lg px-2 text-sm font-medium transition-colors ${active ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                  {page}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryLocations;
